import { useEffect, useState } from 'react';

/**
 * Real `window.matchMedia('(min-width: 960px)')` viewport query (prototype's
 * `WIDE_MQ`, docs/lead-scanner-prototype.html ~line 728) — drives the
 * responsive master-detail shell shared by `ViewSwitcher.tsx` and the
 * unified Scans page (`ScansPage.tsx`, plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md). Extracted out of
 * `ViewSwitcher.tsx` (where it used to be the only consumer) once a second
 * surface needed the exact same wide/narrow master-detail behavior.
 */
const WIDE_VIEWPORT_QUERY = '(min-width: 960px)';

export function useIsWideViewport(): boolean {
  const getMatches = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(WIDE_VIEWPORT_QUERY).matches
      : false;

  const [isWide, setIsWide] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(WIDE_VIEWPORT_QUERY);
    // Re-sync on every effect run in case the query somehow differs from the
    // lazy-initial-state snapshot by the time this runs.
    setIsWide(mql.matches);

    const handler = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mql.addEventListener('change', handler);
    // No lifetime ref here — each effect run subscribes fresh and its own
    // cleanup unconditionally removes exactly that listener, so StrictMode's
    // dev-only setup→cleanup→setup double-invoke leaves exactly one live
    // listener, same as a real single mount. The StrictMode trap
    // (docs/v4-system/strictmode-resilient-effects.md) is a REF whose
    // cleanup sets it but whose setup never resets it — there is no such ref
    // to get stuck here, by construction.
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isWide;
}
